/**
 * Painel da tarefa: estado, ações conforme o status e a permissão (cancelar
 * e reabrir são de quem gere), edição (título, descrição, quem, onde,
 * prazos), comentários e o histórico da tarefa.
 */
import React, { useEffect, useMemo, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { listarEventosTarefa } from "../../../services/mapaProducao";
import { formatarDataLocal } from "../../../utils/dinheiro";
import { montarTimelineAmbiente } from "../dados/timeline";
import {
  ROTULO_STATUS_TAREFA,
  TOM_STATUS_TAREFA,
  acoesDaTarefa,
  tarefaAberta,
  tarefaAtrasada,
} from "../dados/vocabulario";
import { CampoColaborador, CamposLugar, limparEspacos } from "./formularios";
import { Acoes, Campo, Campos, Dados, Secao, Selos, Vazio } from "./styles";
import { ListaTimeline } from "./Timeline";
import { ErroDaAcao, PillStatus, useErroDeAcao } from "./ui";

const TAMANHO_TITULO = 200;
const TAMANHO_DESCRICAO = 4000;
const TAMANHO_COMENTARIO = 4000;

const SUCESSO_DA_ACAO = {
  iniciar: "Tarefa iniciada.",
  pausar: "Tarefa pausada.",
  retomar: "Tarefa retomada.",
  concluir: "Tarefa concluída.",
  cancelar: "Tarefa cancelada.",
  reabrir: "Tarefa reaberta.",
};

const quandoOuTraco = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "Ainda não";
const diaOuTraco = (iso) => (iso ? formatarDataLocal(iso) : "Sem data");

const SEM_HISTORICO = { eventos: [], erro: null };

/**
 * Histórico da tarefa. O guardado anda SEMPRE junto do id de quem foi lido:
 * abrir outra tarefa não pode mostrar os eventos da anterior enquanto a
 * leitura nova não chega. Reler a MESMA tarefa (`gatilho`) mantém a lista.
 */
function useEventosDaTarefa(tarefaId, aberto, gatilho) {
  const { request } = useAuth();
  const [lido, setLido] = useState({ tarefaId: null, ...SEM_HISTORICO });
  useEffect(() => {
    if (!aberto || !tarefaId) return undefined;
    let vivo = true;
    Promise.resolve()
      .then(() => listarEventosTarefa(request, tarefaId))
      .then((lista) => {
        if (vivo) setLido({ tarefaId, eventos: Array.isArray(lista) ? lista : [], erro: null });
      })
      .catch((e) => {
        if (!vivo) return;
        const erro = e?.message || "Não foi possível ler o histórico.";
        setLido((atual) => ({ tarefaId, eventos: atual.tarefaId === tarefaId ? atual.eventos : [], erro }));
      });
    return () => {
      vivo = false;
    };
  }, [request, tarefaId, aberto, gatilho]);
  return tarefaId && lido.tarefaId === tarefaId ? { eventos: lido.eventos, erro: lido.erro } : SEM_HISTORICO;
}

function AcoesDaTarefa({ tarefa, podeOperar, podeGerir, acoes, rodar }) {
  const [pendente, setPendente] = useState(null);
  const [motivo, setMotivo] = useState("");
  const disponiveis = acoesDaTarefa(tarefa.status, { podeOperar, podeGerir });
  useEffect(() => {
    setPendente(null);
    setMotivo("");
  }, [tarefa.status]);
  if (!disponiveis.length) return null;

  const executar = (acao, texto = "") => rodar(() => acoes.acaoTarefa(tarefa, acao.acao, texto));

  if (pendente) {
    const faltaMotivo = pendente.exigeMotivo && !motivo.trim();
    return (
      <Secao aria-label="Ações da tarefa">
        <h3>{pendente.rotulo}</h3>
        <Campo>
          {pendente.exigeMotivo ? "Motivo (obrigatório)" : "Motivo (opcional)"}
          <textarea value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
        </Campo>
        <Acoes>
          <AsyncButton
            variant={pendente.perigosa ? "danger" : "primary"}
            silentError
            disabled={faltaMotivo}
            successMessage={SUCESSO_DA_ACAO[pendente.acao]}
            onClick={() => executar(pendente, motivo.trim()).then(() => setPendente(null))}
          >
            Confirmar: {pendente.rotulo.toLowerCase()}
          </AsyncButton>
          <button type="button" className="btn btn--secondary" onClick={() => setPendente(null)}>
            Voltar
          </button>
        </Acoes>
      </Secao>
    );
  }

  return (
    <Secao aria-label="Ações da tarefa">
      <h3>Ações</h3>
      <Acoes>
        {disponiveis.map((acao) =>
          acao.pedeMotivo ? (
            <button
              key={acao.acao}
              type="button"
              className={acao.perigosa ? "btn btn--ghost" : "btn btn--secondary"}
              style={acao.perigosa ? { color: "var(--danger-text)" } : undefined}
              onClick={() => setPendente(acao)}
            >
              {acao.rotulo}
            </button>
          ) : (
            <AsyncButton
              key={acao.acao}
              variant={acao.acao === "concluir" || acao.acao === "iniciar" ? "primary" : "secondary"}
              silentError
              successMessage={SUCESSO_DA_ACAO[acao.acao]}
              onClick={() => executar(acao)}
            >
              {acao.rotulo}
            </AsyncButton>
          )
        )}
      </Acoes>
    </Secao>
  );
}

function valoresDaTarefa(tarefa) {
  return {
    titulo: tarefa.titulo || "",
    descricao: tarefa.descricao || "",
    colaboradorId: tarefa.colaborador_id || null,
    subsetorId: tarefa.subsetor_id || null,
    postoId: tarefa.posto_id || null,
    prevista: tarefa.data_prevista || "",
    limite: tarefa.data_limite || "",
    motivo: "",
  };
}

// Só o que mudou vai no PATCH (o backend registra um evento por mudança).
function camposAlterados(tarefa, form) {
  const antes = valoresDaTarefa(tarefa);
  const mapa = {
    titulo: ["titulo", limparEspacos(form.titulo)],
    descricao: ["descricao", form.descricao.trim()],
    colaboradorId: ["colaborador_id", form.colaboradorId || null],
    subsetorId: ["subsetor_id", form.subsetorId || null],
    postoId: ["posto_id", form.postoId || null],
    prevista: ["data_prevista", form.prevista || null],
    limite: ["data_limite", form.limite || null],
  };
  const campos = {};
  Object.entries(mapa).forEach(([chave, [campo, valor]]) => {
    const anterior = chave === "titulo" || chave === "descricao" ? antes[chave].trim() : antes[chave] || null;
    if ((valor || null) !== (anterior || null)) campos[campo] = valor;
  });
  return campos;
}

function EditarTarefa({ tarefa, estado, podeEditar, acoes, rodar }) {
  const [form, setForm] = useState(() => valoresDaTarefa(tarefa));
  const versaoDaTarefa = `${tarefa.id}:${tarefa.versao}`;
  useEffect(() => {
    // Recomeça só quando a tarefa gravada muda (versão nova), não a cada leitura.
    setForm(valoresDaTarefa(tarefa));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versaoDaTarefa]);
  const campos = camposAlterados(tarefa, form);
  const mudouPrazo = "data_prevista" in campos || "data_limite" in campos;
  const semTitulo = !limparEspacos(form.titulo);
  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  return (
    <Secao aria-label="Editar tarefa">
      <h3>Dados da tarefa</h3>
      <Campo>
        Título
        <input
          value={form.titulo}
          maxLength={TAMANHO_TITULO}
          onChange={mudar("titulo")}
          onBlur={() => setForm((f) => ({ ...f, titulo: limparEspacos(f.titulo) }))}
          disabled={!podeEditar}
        />
      </Campo>
      <CampoColaborador
        setorId={tarefa.setor_id}
        colaboradores={estado?.colaboradores}
        valor={form.colaboradorId}
        onChange={(colaboradorId) => setForm((f) => ({ ...f, colaboradorId }))}
        desabilitado={!podeEditar}
      />
      <CamposLugar
        setorId={tarefa.setor_id}
        estado={estado}
        subsetorId={form.subsetorId}
        postoId={form.postoId}
        onChange={(lugar) => setForm((f) => ({ ...f, ...lugar }))}
        desabilitado={!podeEditar}
      />
      <Campos $min={160}>
        <Campo>
          Previsão
          <input type="date" value={form.prevista} onChange={mudar("prevista")} disabled={!podeEditar} />
        </Campo>
        <Campo>
          Data limite
          <input type="date" value={form.limite} onChange={mudar("limite")} disabled={!podeEditar} />
        </Campo>
      </Campos>
      {podeEditar && mudouPrazo && (
        <Campo>
          Motivo da mudança de prazo (opcional)
          <textarea value={form.motivo} maxLength={500} onChange={mudar("motivo")} />
        </Campo>
      )}
      <Campo>
        Descrição
        <textarea
          value={form.descricao}
          maxLength={TAMANHO_DESCRICAO}
          onChange={mudar("descricao")}
          disabled={!podeEditar}
        />
      </Campo>
      {podeEditar && (
        <Acoes>
          <AsyncButton
            silentError
            disabled={semTitulo || Object.keys(campos).length === 0}
            successMessage="Tarefa atualizada."
            onClick={() => rodar(() => acoes.atualizarTarefa(tarefa, campos, form.motivo.trim()))}
          >
            Salvar alterações
          </AsyncButton>
          {semTitulo && <span style={{ fontSize: 12, color: "var(--danger-text)" }}>O título não pode ficar vazio.</span>}
        </Acoes>
      )}
    </Secao>
  );
}

function ComentarTarefa({ tarefa, acoes, rodar, onComentou }) {
  const [texto, setTexto] = useState("");
  return (
    <Secao aria-label="Comentar na tarefa">
      <h3>Comentário</h3>
      <Campo>
        <textarea
          value={texto}
          maxLength={TAMANHO_COMENTARIO}
          onChange={(e) => setTexto(e.target.value)}
          aria-label="Comentário da tarefa"
        />
      </Campo>
      <Acoes>
        <AsyncButton
          variant="secondary"
          silentError
          disabled={!texto.trim()}
          successMessage="Comentário registrado."
          onClick={() =>
            rodar(async () => {
              await acoes.comentarTarefa(tarefa, texto.trim());
              setTexto("");
              onComentou();
            })
          }
        >
          Comentar
        </AsyncButton>
      </Acoes>
    </Secao>
  );
}

/**
 * Props: aberto, tarefa (atual) ou null, indice, estado, setorNome,
 * podeOperar, podeGerir, hoje, acoes, onFechar, onAbrirAmbiente(chave) e
 * onAbrirPeca(peca) (null = sem link), onRecarregar.
 */
export default function DetalheTarefa({
  aberto,
  tarefa,
  indice,
  estado,
  setorNome = "",
  podeOperar,
  podeGerir,
  hoje,
  acoes,
  onFechar,
  onAbrirAmbiente,
  onAbrirPeca,
  onRecarregar,
}) {
  const { erro, rodar, limpar } = useErroDeAcao();
  const [gatilho, setGatilho] = useState(0);
  const { eventos, erro: erroHistorico } = useEventosDaTarefa(tarefa?.id, aberto, `${tarefa?.versao}:${gatilho}`);
  const timeline = useMemo(() => (tarefa ? montarTimelineAmbiente(null, eventos, [tarefa]) : []), [tarefa, eventos]);
  useEffect(() => {
    if (!aberto) limpar();
  }, [aberto, limpar]);

  const titulo = tarefa ? `${tarefa.codigo} · ${tarefa.titulo}` : "Tarefa";
  const subtitulo = tarefa ? [ROTULO_STATUS_TAREFA[tarefa.status], setorNome].filter(Boolean).join(" · ") : "";
  const aberta = tarefa && tarefaAberta(tarefa);
  const atrasada = tarefa && tarefaAtrasada(tarefa, hoje);
  const peca = tarefa?.peca_id ? indice.pecas.get(tarefa.peca_id) || null : null;

  return (
    <SlideOverPanel open={aberto} onClose={onFechar} title={titulo} subtitle={subtitulo} width="min(96vw, 620px)">
      {!tarefa ? (
        <Vazio>Tarefa não encontrada. Ela pode ter sido encerrada há mais de 24 horas.</Vazio>
      ) : (
        <>
          <ErroDaAcao erro={erro} onRecarregar={onRecarregar} />
          <Secao aria-label="Resumo da tarefa">
            <Selos>
              <PillStatus tom={atrasada ? "danger" : TOM_STATUS_TAREFA[tarefa.status]}>
                {ROTULO_STATUS_TAREFA[tarefa.status]}
                {atrasada ? " · atrasada" : ""}
              </PillStatus>
            </Selos>
            {tarefa.motivo_pausa && tarefa.status === "pausada" && <p className="nota">Pausada: {tarefa.motivo_pausa}</p>}
            {tarefa.motivo_cancelamento && tarefa.status === "cancelada" && (
              <p className="nota">Cancelada: {tarefa.motivo_cancelamento}</p>
            )}
            <Dados>
              <div>
                <dt>Colaborador</dt>
                <dd>{indice.colaboradores.get(tarefa.colaborador_id)?.nome || "Sem colaborador"}</dd>
              </div>
              <div>
                <dt>Ambiente</dt>
                <dd>
                  {tarefa.ambiente_rotulo ? (
                    onAbrirAmbiente ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        style={{ padding: 0, height: "auto" }}
                        onClick={() => onAbrirAmbiente(tarefa.ambiente_chave)}
                      >
                        {tarefa.ambiente_rotulo}
                      </button>
                    ) : (
                      tarefa.ambiente_rotulo
                    )
                  ) : (
                    "Sem ambiente"
                  )}
                </dd>
              </div>
              <div>
                <dt>Peça desmembrada</dt>
                <dd>
                  {peca ? (
                    onAbrirPeca ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        style={{ padding: 0, height: "auto" }}
                        onClick={() => onAbrirPeca(peca)}
                      >
                        {peca.codigo} · {peca.nome}
                      </button>
                    ) : (
                      `${peca.codigo} · ${peca.nome}`
                    )
                  ) : (
                    "Nenhuma"
                  )}
                </dd>
              </div>
              <div>
                <dt>Previsão</dt>
                <dd>{diaOuTraco(tarefa.data_prevista)}</dd>
              </div>
              <div>
                <dt>Limite</dt>
                <dd>{diaOuTraco(tarefa.data_limite)}</dd>
              </div>
              <div>
                <dt>Criada</dt>
                <dd>
                  {quandoOuTraco(tarefa.criado_em)}
                  {tarefa.criado_por ? ` por ${tarefa.criado_por}` : ""}
                </dd>
              </div>
              <div>
                <dt>Iniciada</dt>
                <dd>{quandoOuTraco(tarefa.iniciado_em)}</dd>
              </div>
              <div>
                <dt>Concluída</dt>
                <dd>{quandoOuTraco(tarefa.concluido_em)}</dd>
              </div>
            </Dados>
          </Secao>
          <AcoesDaTarefa tarefa={tarefa} podeOperar={podeOperar} podeGerir={podeGerir} acoes={acoes} rodar={rodar} />
          <EditarTarefa tarefa={tarefa} estado={estado} podeEditar={podeOperar && aberta} acoes={acoes} rodar={rodar} />
          {podeOperar && (
            <ComentarTarefa tarefa={tarefa} acoes={acoes} rodar={rodar} onComentou={() => setGatilho((n) => n + 1)} />
          )}
          <Secao aria-label="Histórico da tarefa">
            <h3>Histórico</h3>
            {erroHistorico && <p className="nota">{erroHistorico}</p>}
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
              <ListaTimeline itens={timeline} compacto hoje={hoje} vazio="Sem registros ainda." />
            </div>
          </Secao>
        </>
      )}
    </SlideOverPanel>
  );
}
